import { Button, Flex, Stack, Text, Tooltip, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import { WorkshopVerificationRequest } from 'api/types';
import { AxiosError } from 'axios';
import Link from 'components/Link';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import AlertModal from '../../components/modals/AlertModal';
import { StatusTag } from '../../components/StatusTag';
import { DataTable } from '../../components/Table/DataTable';
import { useUserContext } from '../../context/UserContextProvider';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { defaultToastOptions } from '../../helpers/manageErrorResponse';
import useCreateWorkshopVerificationMutation from '../../hooks/mutations/useCreateWorkshopVerificationMutation';
import useDeleteWorkshopVerificationRequestMutation from '../../hooks/mutations/useDeleteWorkshopVerificationRequestMutation';
import useWorkshopUpdateRequestQuery from '../../hooks/useWorkshopUpdateRequestQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopVerificationTableCard = (props: { workshopUpdateRequestId: number }) => {
  const { workshopUpdateRequestId } = props;
  const { t } = useTranslation();
  const workshopUpdateRequestQuery = useWorkshopUpdateRequestQuery(workshopUpdateRequestId);

  const userContext = useUserContext();
  const statusCodes = userContext.statusCodes?.workshop_verification_request;
  const verifications = workshopUpdateRequestQuery.data?.verifications as unknown as WorkshopVerificationRequest;
  const disableVerificationRequest =
    workshopUpdateRequestQuery.data?.status === 700 ||
    workshopUpdateRequestQuery.data?.status === 900 ||
    (workshopUpdateRequestQuery.data as any)?.verifications[0]?.status === 700 ||
    (workshopUpdateRequestQuery.data as any)?.verifications[0]?.status === 0 ||
    (workshopUpdateRequestQuery.data as any)?.verifications[0]?.status === 100 ||
    (workshopUpdateRequestQuery.data as any)?.verifications[0]?.status === 200 ||
    (workshopUpdateRequestQuery.data as any)?.verifications[0]?.status === 150 ||
    (workshopUpdateRequestQuery.data as any)?.verifications[0]?.status === 250;

  const workshopId = workshopUpdateRequestQuery.data?.workshop_id as number;
  const updateRequestId = workshopUpdateRequestQuery.data?.id;

  const createWorkshopVerificationMutation = useCreateWorkshopVerificationMutation({
    workshop_id: workshopId,
    workshop_update_request_id: updateRequestId,
  });

  const deleteWorkshopVerificationRequestMutation = useDeleteWorkshopVerificationRequestMutation(['workshop', workshopId]);
  const queryClient = useQueryClient();
  const toast = useToast(defaultToastOptions);
  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'user_readable_id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Link data-test-id={'sr-' + row?.index} to={`/app/op/workshop-verification-request/${row.original.id}`}>
              {row.original.user_readable_id}
            </Link>
          );
        },
      },

      {
        Header: t('forms:status.label'),
        accessor: (row: { status: number }) => {
          return (
            <StatusTag
              resourceKey={'workshop_verification_request'}
              //@ts-ignore
              statusCodes={statusCodes?.workshop_verification_request}
              //@ts-ignore
              currentStatus={row?.status}
            />
          );
        },
      },
      {
        Header: t('forms:data_table.created_at'),
        accessor: (row: { created_at: string }) => {
          return <>{dateTimeTransformer(row.created_at)}</>;
        },
      },
      {
        id: 'delete',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const inStatusCreated = row.original.status === 0;
          if (!row.original.workshop_user_id) {
            return (
              inStatusCreated && (
                <Flex alignContent={'flex-end'}>
                  <Button
                    ml={'auto'}
                    onClick={() => {
                      NiceModal.show(AlertModal as any, {
                        onSubmit: () =>
                          deleteWorkshopVerificationRequestMutation.mutate(row.original.id, {
                            onSuccess: () => {
                              return queryClient.invalidateQueries({ queryKey: useWorkshopUpdateRequestQuery.getKey(workshopUpdateRequestId) });
                            },
                            onError: (error: AxiosError<{ message: string }>) => {
                              toast({
                                title: error.response?.data?.message ?? t('errors:general'),
                                status: 'error',
                              });
                            },
                          }),
                        children: t('workshop_verification_request:modals.delete.description'),
                        content: {
                          header: t('workshop_verification_request:modals.delete.header'),
                          footer: {
                            buttons: {
                              cancelCaption: t('common:cancel'),
                              actionCaption: t('common:delete'),
                            },
                          },
                        },
                      });
                    }}
                  >
                    {t('common:delete')}
                  </Button>
                </Flex>
              )
            );
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [verifications],
  );
  if (workshopUpdateRequestQuery.isLoading) {
    return <LoadingSpinner />;
  }
  return (
    <ContentCard isLoading={workshopUpdateRequestQuery.isFetching} minH="fit-content" header={t('workshop_update_request:reverify.card.header')}>
      {
        <>
          <Flex alignItems={'center'} justifyContent={'space-between'} mb={'10'}>
            <Text flex={5} align="start">
              {t('workshop_update_request:reverify.description')}
            </Text>
            <div style={{ flex: 0.5 }}></div>

            <Tooltip label={t('workshop_update_request:reverify.created')} shouldWrapChildren isDisabled={!disableVerificationRequest}>
              <Button
                flex={2.5}
                variant="primary"
                w={'full'}
                whiteSpace={'pre-wrap'}
                disabled={disableVerificationRequest}
                onClick={() => {
                  NiceModal.show(AlertModal as any, {
                    onSubmit: () => createWorkshopVerificationMutation.mutate(),
                    children: t('workshop_update_request:reverify.modal.description'),
                    content: {
                      header: t('workshop_update_request:reverify.modal.header'),
                      footer: {
                        buttons: {
                          cancelCaption: t('common:cancel'),
                          actionCaption: t('workshop_update_request:reverify.modal.action'),
                        },
                      },
                    },
                  });
                }}
              >
                {t('workshop_update_request:reverify.action')}
              </Button>
            </Tooltip>
          </Flex>
        </>
      }
      <Stack direction={['column', 'row']} w={'100%'} mt={'10'}>
        <Stack direction="column" w={'100%'}>
          <DataTable
            data-test-id="service-records-table"
            data={verifications || []}
            // @ts-ignore
            filtersInitiallyOpen
            autoFocus={true}
            hasFilters={false}
            hasSearchInput={false}
            columns={columns}
            loading={workshopUpdateRequestQuery.isLoading}
            isMoreThenOnePage={false}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopVerificationTableCard;
